import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { ShoppingCart, Package, Users, ChevronLeft, Plus, Minus, Download } from "lucide-react";

const MySwal = withReactContent(Swal);

const API_BASE_URL = "https://backend.dlapakistan.com/api";

const API_ENDPOINTS = {
  DLA_SHOP_PRODUCT_DETAIL: (id) => `${API_BASE_URL}/dla-shop/products/${id}/`,
  DLA_SHOP_PRODUCTS: `${API_BASE_URL}/dla-shop/products/`,
  DLA_SHOP_BASKET_ADD: `${API_BASE_URL}/dla-shop/basket/add_item/`,
  DLA_SHOP_BASKET_COUNT: `${API_BASE_URL}/dla-shop/basket/count/`,
  DLA_SHOP_PRODUCT_FILES: (id) => `${API_BASE_URL}/dla-shop/products/${id}/files/`,
};

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [productFiles, setProductFiles] = useState([]);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingToBasket, setAddingToBasket] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [isMember, setIsMember] = useState(false);
  const [memberToken, setMemberToken] = useState(null);

  const GREEN = "#70B444";
  const GREEN_DARK = "#5A9437";
  const GREEN_LIGHT = "#F0F7EC";

  // Function to get CSRF token from cookies
  const getCsrfToken = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrftoken') {
        return value;
      }
    }
    return '';
  };

  // Create headers for fetch requests
  const createHeaders = () => {
    const csrfToken = getCsrfToken();
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-CSRFToken': csrfToken,
    };
    
    if (memberToken) {
      headers['Authorization'] = `Bearer ${memberToken}`;
    }
    
    return headers;
  };

  // Check member status from localStorage
  useEffect(() => {
    const token = localStorage.getItem("member_token");
    const memberData = localStorage.getItem("member_data");
    setIsMember(!!(token && memberData));
    setMemberToken(token);
    console.log("👤 Member status:", !!token);
  }, []);

  const ensureSingleSession = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.DLA_SHOP_BASKET_COUNT, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log("✅ Session initialized, count:", data.count);
        return true;
      }
    } catch (error) {
      console.error("❌ Session error:", error);
    }
    return false;
  };

  const updateBasketCount = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.DLA_SHOP_BASKET_COUNT, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        window.dispatchEvent(new CustomEvent('basketUpdated', { 
          detail: { count: data.count || 0 } 
        }));
      }
    } catch (error) {
      console.error("Error updating basket count:", error);
    }
  };

  const fetchProductFiles = async (productId) => {
    try {
      const response = await fetch(API_ENDPOINTS.DLA_SHOP_PRODUCT_FILES(productId));
      if (response.ok) {
        const data = await response.json();
        return data.data || [];
      }
    } catch (error) {
      console.error(`Error fetching files for product ${productId}:`, error);
    }
    return [];
  };

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.DLA_SHOP_PRODUCT_DETAIL(id));
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const productData = await response.json();
      
      // Fetch product files
      const files = await fetchProductFiles(productData.id);
      setProductFiles(files);
      
      setProduct({
        ...productData,
        price: productData.price || "0.00",
        amount: productData.amount || 0,
        category_title: productData.category_title || "General",
        is_free_for_members: productData.is_free_for_members || false,
        description: productData.description || "",
        thumbnail_url: productData.thumbnail_url || productData.image || "",
        files: files,
      });

      const allProductsResponse = await fetch(API_ENDPOINTS.DLA_SHOP_PRODUCTS);
      if (allProductsResponse.ok) {
        const allData = await allProductsResponse.json();
        let allProducts = [];
        if (Array.isArray(allData)) allProducts = allData;
        else if (allData?.results) allProducts = allData.results;
        else if (allData?.data) allProducts = allData.data;

        const publishedProducts = allProducts.filter(p => p.is_published !== false);
        const similar = publishedProducts
          .filter(p => p.id !== productData.id && p.category === productData.category)
          .slice(0, 4)
          .map(p => ({
            ...p,
            price: p.price || "0.00",
            amount: p.amount || 0,
            category_title: p.category_title || "General",
            is_free_for_members: p.is_free_for_members || false,
            description: p.description || "",
            thumbnail_url: p.thumbnail_url || p.image || "",
          }));

        setSimilarProducts(similar);
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      MySwal.fire({
        title: "Error",
        text: "Failed to load product details",
        icon: "error",
        confirmButtonColor: GREEN,
      }).then(() => navigate("/dlashop"));
    } finally {
      setLoading(false);
    }
  };

  const calculateUserPrice = () => {
    if (!product) return 0;
    if (isMember && product.is_free_for_members) return 0;
    return parseFloat(product.price) || 0;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDownloadFile = async () => {
    if (!isMember) {
      MySwal.fire({
        title: "Members Only",
        text: "Please login as a member to download files",
        icon: "warning",
        confirmButtonColor: GREEN,
        confirmButtonText: "Login",
        showCancelButton: true,
        cancelButtonText: "Cancel"
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/member-login");
        }
      });
      return;
    }

    if (!productFiles || productFiles.length === 0) {
      MySwal.fire({
        title: "No Files",
        text: "This product has no files to download",
        icon: "info",
        confirmButtonColor: GREEN,
      });
      return;
    }

    setDownloadingFile(true);
    
    try {
      console.log(`📥 Downloading files for product: ${product.title}`);
      let downloadedCount = 0;
      
      // Download all files
      for (const file of productFiles) {
        try {
          // Construct the correct file URL
          let fileUrl = file.file_url || file.file;
          
          // If it's a relative path, make it absolute
          if (fileUrl && fileUrl.startsWith('/')) {
            fileUrl = `https://backend.dlapakistan.com${fileUrl}`;
          } else if (!fileUrl && file.file_name) {
            // Try to construct from file_name
            fileUrl = `https://backend.dlapakistan.com/media/dla_shop/product_files/${file.file_name}`;
          } else if (!fileUrl) {
            console.warn("No file URL found for file:", file);
            continue;
          }

          console.log("Downloading from:", fileUrl);
          
          const response = await fetch(fileUrl, {
            headers: memberToken ? {
              'Authorization': `Bearer ${memberToken}`
            } : {},
            credentials: 'include'
          });
          
          if (!response.ok) {
            console.error(`Failed to download ${file.file_name}:`, response.status);
            continue;
          }
          
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = file.file_name || `file-${Date.now()}.bin`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          
          downloadedCount++;
          
          // Small delay between downloads
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (fileError) {
          console.error("Error downloading individual file:", fileError);
        }
      }
      
      if (downloadedCount > 0) {
        MySwal.fire({
          title: "Download Started!",
          html: `<div style="text-align: center;">
                  <div style="color: ${GREEN}; font-size: 40px;">✓</div>
                  <p style="font-weight: 600;">${product.title}</p>
                  <p style="color: #666;">${downloadedCount} file${downloadedCount > 1 ? 's' : ''} downloaded</p>
                </div>`,
          showConfirmButton: false,
          timer: 2000,
        });
      } else {
        throw new Error("No files could be downloaded");
      }
      
    } catch (error) {
      console.error("❌ Download error:", error);
      MySwal.fire({
        title: "Download Failed",
        text: "Failed to download files. Please try again.",
        icon: "error",
        confirmButtonColor: GREEN,
      });
    } finally {
      setDownloadingFile(false);
    }
  };

  const handleAddToBasket = async () => {
    if (quantity > product.amount) {
      MySwal.fire({
        title: "Insufficient Stock",
        text: `Only ${product.amount} items available in stock.`,
        icon: "warning",
        confirmButtonColor: GREEN,
      });
      return;
    }

    if (parseInt(product.amount) <= 0) {
      MySwal.fire({
        title: "Out of Stock",
        text: "This product is currently out of stock.",
        icon: "error",
        confirmButtonColor: GREEN,
      });
      return;
    }

    setAddingToBasket(true);
    
    try {
      console.log("🛒 Adding product:", product.id, "quantity:", quantity, "Member:", isMember);
      
      const headers = createHeaders();
      
      const response = await fetch(API_ENDPOINTS.DLA_SHOP_BASKET_ADD, {
        method: 'POST',
        credentials: 'include',
        headers: headers,
        body: JSON.stringify({ product_id: product.id, quantity: quantity })
      });

      const data = await response.json();
      
      if (!response.ok) {
        // If CSRF error, retry once
        if (response.status === 403 && data.detail?.includes('CSRF')) {
          console.log("🔄 CSRF error, retrying...");
          // Refresh CSRF token
          await fetch(API_ENDPOINTS.DLA_SHOP_BASKET_COUNT, {
            credentials: 'include'
          });
          
          // Retry with new token
          const retryResponse = await fetch(API_ENDPOINTS.DLA_SHOP_BASKET_ADD, {
            method: 'POST',
            credentials: 'include',
            headers: createHeaders(),
            body: JSON.stringify({ product_id: product.id, quantity: quantity })
          });
          
          if (retryResponse.ok) {
            const retryData = await retryResponse.json();
            await handleSuccessfulAdd(retryData);
            return;
          }
        }
        throw new Error(data.error || data.detail || 'Failed to add to basket');
      }
      
      await handleSuccessfulAdd(data);
      
    } catch (error) {
      console.error("❌ Error adding to basket:", error);
      MySwal.fire({
        title: "Error",
        text: error.message || "Failed to add product to basket.",
        icon: "error",
        confirmButtonColor: GREEN,
      });
    } finally {
      setAddingToBasket(false);
    }
  };

  const handleSuccessfulAdd = async (data) => {
    setProduct(prev => ({ 
      ...prev, 
      amount: Math.max(0, prev.amount - quantity) 
    }));
    
    const itemCount = data.total_items || 0;
    
    window.dispatchEvent(new CustomEvent('basketUpdated', { 
      detail: { count: itemCount } 
    }));
    
    const userPrice = calculateUserPrice();
    const totalPrice = userPrice * quantity;
    
    if (isMember && product.is_free_for_members) {
      MySwal.fire({
        title: "Added to Cart!",
        html: `<div style="text-align: center;">
                <div style="color: ${GREEN}; font-size: 40px; margin-bottom: 10px;">✓</div>
                <p style="font-weight: 600; margin-bottom: 5px; color: #1F2937;">${product.title}</p>
                <p style="color: #666; margin-bottom: 5px;">Quantity: ${quantity}</p>
                <div style="background: ${GREEN_LIGHT}; padding: 10px; border-radius: 8px; margin: 10px 0;">
                  <p style="color: ${GREEN_DARK}; font-weight: 600;">✨ FREE for Members!</p>
                  <p style="color: #666; text-decoration: line-through;">Regular: £${parseFloat(product.price).toFixed(2)} each</p>
                </div>
                <p style="color: #666;">Added to your cart</p>
              </div>`,
        showConfirmButton: false,
        timer: 2000,
        background: '#fff',
      });
    } else {
      MySwal.fire({
        title: "Added to Cart!",
        html: `<div style="text-align: center;">
                <div style="color: ${GREEN}; font-size: 40px; margin-bottom: 10px;">✓</div>
                <p style="font-weight: 600; margin-bottom: 5px; color: #1F2937;">${product.title}</p>
                <p style="color: #666; margin-bottom: 5px;">Quantity: ${quantity}</p>
                <p style="color: #666; margin-bottom: 5px; font-size: 18px; font-weight: bold;">Total: £${totalPrice.toFixed(2)}</p>
                <p style="color: #666;">Added to your cart</p>
              </div>`,
        showConfirmButton: false,
        timer: 1500,
        background: '#fff',
      });
    }
  };

  const increaseQuantity = () => {
    if (product && quantity < product.amount) {
      setQuantity(prev => prev + 1);
    }
  };
  
  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  };

  useEffect(() => {
    ensureSingleSession().then(() => {
      fetchProductDetails();
    });
  }, [id]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#70B444]"></div>
        <p className="mt-4 text-gray-600">Loading product details...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="text-red-600 mb-4">Product not found</div>
        <button 
          onClick={() => navigate("/dlashop")} 
          className="px-4 py-2 rounded-lg text-white font-medium hover:opacity-90 transition-opacity" 
          style={{ background: GREEN }}
        >
          Back to Products
        </button>
      </div>
    );
  }

  const userPrice = calculateUserPrice();
  const isFreeForMember = isMember && product.is_free_for_members;
  const hasImage = product.thumbnail_url || product.image;
  const isOutOfStock = parseInt(product.amount) <= 0;
  const hasFiles = productFiles && productFiles.length > 0;
  const imageFiles = productFiles.filter(f => f.is_image) || [];
  const displayImage = product.thumbnail_url || product.image || (imageFiles.length > 0 ? imageFiles[0].file_url : null);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 bg-white min-h-screen font-sans">
      <button 
        onClick={() => navigate("/dlashop")} 
        className="flex items-center gap-2 text-gray-500 hover:text-[#70B444] mb-10 text-sm transition-all"
      >
        <ChevronLeft className="w-4 h-4" /> BACK TO ALL PRODUCTS
      </button>

      {/* Member status banner - informational only */}
      {isMember && (
        <div className="mb-6 p-3 rounded-lg" style={{ background: GREEN_LIGHT }}>
          <p className="text-sm font-medium" style={{ color: GREEN_DARK }}>
            <Users className="w-4 h-4 inline mr-1" />
            You are logged in as a member. Free products will show as £0.00 at checkout!
          </p>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-16 mb-16">
        <div className="w-full md:w-1/2 flex justify-center">
          <div className="relative w-full max-w-md">
            {/* Member FREE badge - purely visual */}
            {product.is_free_for_members && (
              <div className="absolute top-4 left-4 z-10">
                <span 
                  className="px-4 py-2 text-white text-sm font-bold rounded-full shadow-lg flex items-center gap-1.5"
                  style={{ background: isMember ? GREEN : GREEN_DARK }}
                >
                  <Users className="w-3 h-3" />
                  {isMember ? "FREE for Members" : "Member Exclusive"}
                </span>
              </div>
            )}

            {/* File count badge */}
            {hasFiles && isMember && (
              <div className="absolute top-4 right-4 z-10">
                <span className="bg-black/60 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 backdrop-blur-sm">
                  <Download className="w-3 h-3" />
                  {productFiles.length} {productFiles.length === 1 ? 'file' : 'files'}
                </span>
              </div>
            )}

            {displayImage ? (
              <img 
                src={displayImage} 
                alt={product.title} 
                className="w-full h-auto object-contain rounded-lg" 
              />
            ) : (
              <div className="aspect-square bg-gray-50 flex items-center justify-center border border-dashed border-gray-300 rounded-lg">
                <Package className="w-16 h-16 text-gray-300" />
              </div>
            )}
          </div>
        </div>

        <div className="w-full md:w-1/2">
          <div className="mb-4">
            <span 
              className="inline-block px-3 py-1 text-sm font-bold rounded-full text-white mb-2" 
              style={{ background: GREEN }}
            >
              {product.category_title}
            </span>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-8">{product.title}</h1>
          
          <div className="space-y-4 mb-8">
            <div className="flex items-center gap-4">
              <span className="text-lg font-semibold text-gray-700">Price:</span>
              {isFreeForMember ? (
                <>
                  <span className="text-2xl font-bold line-through text-gray-400">
                    £{parseFloat(product.price).toFixed(2)}
                  </span>
                  <span className="text-2xl font-bold" style={{ color: GREEN }}>FREE</span>
                </>
              ) : (
                <span className="text-2xl font-bold" style={{ color: GREEN }}>
                  £{userPrice.toFixed(2)}
                </span>
              )}
            </div>
            
            {/* Member free message - only for members */}
            {isMember && product.is_free_for_members && (
              <div className="p-3 rounded-lg" style={{ background: GREEN_LIGHT, border: `1px solid ${GREEN}` }}>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" style={{ color: GREEN }} />
                  <span className="font-medium text-sm" style={{ color: GREEN_DARK }}>
                    This product is FREE for you as a DLA member!
                  </span>
                </div>
              </div>
            )}

            {/* Member hint for non-members on free items */}
            {!isMember && product.is_free_for_members && (
              <div className="p-3 rounded-lg" style={{ background: GREEN_LIGHT, border: `1px solid ${GREEN}` }}>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" style={{ color: GREEN }} />
                  <span className="font-medium text-sm" style={{ color: GREEN_DARK }}>
                    This product is FREE for DLA members. Sign in to get it free!
                  </span>
                </div>
              </div>
            )}

            {/* File info for members */}
            {isMember && hasFiles && (
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <Download className="w-4 h-4" style={{ color: GREEN }} />
                  <span className="font-medium text-sm" style={{ color: GREEN_DARK }}>
                    Includes {productFiles.length} downloadable file{productFiles.length > 1 ? 's' : ''}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Total size: {productFiles.reduce((acc, f) => acc + (f.file_size || 0), 0) > 0 
                    ? formatFileSize(productFiles.reduce((acc, f) => acc + (f.file_size || 0), 0))
                    : 'Various sizes'}
                </p>
              </div>
            )}
          </div>

          <div className="mb-6">
            {!isOutOfStock ? (
              <div className="text-sm text-gray-500">{product.amount} in stock</div>
            ) : (
              <div className="text-sm text-red-600">Out of stock</div>
            )}
          </div>

          {!isOutOfStock && !isMember && (
            <div className="flex items-center gap-4 mb-8">
              <span className="text-sm font-medium text-gray-700">Quantity:</span>
              <div className="flex items-center border border-gray-300 rounded">
                <button 
                  onClick={decreaseQuantity} 
                  className="px-4 py-2 hover:bg-gray-100 disabled:opacity-50" 
                  disabled={quantity <= 1}
                >
                  <Minus className="w-4 h-4" />
                </button>
                <div className="w-16 text-center py-2 text-lg font-bold">{quantity}</div>
                <button 
                  onClick={increaseQuantity} 
                  className="px-4 py-2 hover:bg-gray-100 disabled:opacity-50" 
                  disabled={quantity >= product.amount}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Action Button - Download for members with files, Add to Cart for others */}
          {isMember && hasFiles ? (
            <button 
              onClick={handleDownloadFile} 
              disabled={downloadingFile || isOutOfStock}
              className={`w-full md:w-auto px-12 py-4 font-bold rounded shadow-sm transition-colors text-sm uppercase tracking-wider flex items-center justify-center gap-2 mb-8 disabled:opacity-50 disabled:cursor-not-allowed`}
              style={{ 
                background: GREEN, 
                color: 'white' 
              }}
            >
              {downloadingFile ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div> 
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  {isFreeForMember ? 'DOWNLOAD FREE' : 'DOWNLOAD FILES'}
                </>
              )}
            </button>
          ) : (
            <button 
              onClick={handleAddToBasket} 
              disabled={isOutOfStock || addingToBasket}
              className={`w-full md:w-auto px-12 py-4 font-bold rounded shadow-sm transition-colors text-sm uppercase tracking-wider flex items-center justify-center gap-2 mb-8 ${
                isOutOfStock ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'hover:opacity-90'
              }`}
              style={{ 
                background: isOutOfStock ? '#D1D5DB' : GREEN, 
                color: isOutOfStock ? '#6B7280' : 'white' 
              }}
            >
              {addingToBasket ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div> 
                  Adding...
                </>
              ) : (
                <>
                  <ShoppingCart className="w-5 h-5" />
                  {isOutOfStock ? 'OUT OF STOCK' : isFreeForMember ? 'GET FOR FREE' : 'ADD TO BASKET'}
                </>
              )}
            </button>
          )}

          {/* Show message for members without files */}
          {isMember && !hasFiles && (
            <p className="text-sm text-gray-500 mb-8">
              No downloadable files available for this product.
            </p>
          )}

          {/* Price summary for non-members */}
          {!isOutOfStock && !isMember && (
            <div className="text-sm text-gray-600 border-t pt-4">
              {isFreeForMember ? (
                <p className="font-medium" style={{ color: GREEN }}>
                  <Users className="w-4 h-4 inline mr-1" />
                  Total: FREE (You save £{(parseFloat(product.price) * quantity).toFixed(2)})
                </p>
              ) : (
                <p className="font-medium">
                  Total: £{(userPrice * quantity).toFixed(2)}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-16 border-t border-gray-200 pt-12">
        <h2 className="text-3xl font-bold text-gray-800 mb-8">Product Description</h2>
        <div className="prose prose-lg max-w-none">
          {product.description ? (
            <div 
              className="text-gray-600 leading-relaxed" 
              dangerouslySetInnerHTML={{ __html: product.description }} 
            />
          ) : (
            <p className="text-gray-500 italic">No description available.</p>
          )}
        </div>
      </div>

      {/* File List for Members */}
      {isMember && hasFiles && (
        <div className="mt-16 border-t border-gray-200 pt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Download className="w-6 h-6" style={{ color: GREEN }} />
            Downloadable Files
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {productFiles.map((file, index) => (
              <div 
                key={file.id || index} 
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-[#70B444] transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0">
                    {file.is_image ? (
                      <Package className="w-5 h-5 text-blue-500" />
                    ) : file.file_type === 'pdf' ? (
                      <Package className="w-5 h-5 text-red-500" />
                    ) : file.file_type === 'stl' ? (
                      <Package className="w-5 h-5 text-orange-500" />
                    ) : file.file_type === 'zip' ? (
                      <Package className="w-5 h-5 text-yellow-600" />
                    ) : (
                      <Package className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {file.file_name || `File ${index + 1}`}
                    </p>
                    <p className="text-xs text-gray-500">
                      {file.file_size ? formatFileSize(file.file_size) : 'Size unknown'} • {file.file_type || 'file'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleDownloadFile}
                  disabled={downloadingFile}
                  className="ml-2 p-2 text-[#70B444] hover:bg-[#70B444] hover:text-white rounded-lg transition-colors disabled:opacity-50"
                  title="Download"
                >
                  {downloadingFile ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#70B444] border-t-transparent"></div>
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {similarProducts.length > 0 && (
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">Similar Products</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {similarProducts.map((similarProduct) => {
              const isSimilarFreeForMember = isMember && similarProduct.is_free_for_members;
              const similarUserPrice = isSimilarFreeForMember ? 0 : parseFloat(similarProduct.price);
              const hasSimilarImage = similarProduct.thumbnail_url || similarProduct.image;
              
              return (
                <div 
                  key={similarProduct.id} 
                  className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer relative" 
                  onClick={() => navigate(`/dlashop/product/${similarProduct.id}`)}
                >
                  {/* Member FREE badge for similar products */}
                  {similarProduct.is_free_for_members && (
                    <div className="absolute top-2 left-2 z-10">
                      <span 
                        className="px-2 py-1 text-white text-xs font-bold rounded-full shadow-lg flex items-center gap-1"
                        style={{ background: isMember ? GREEN : GREEN_DARK }}
                      >
                        <Users className="w-3 h-3" />
                        {isMember ? "FREE" : "Member"}
                      </span>
                    </div>
                  )}

                  <div className="h-56 bg-gray-50 flex items-center justify-center p-4">
                    {hasSimilarImage ? (
                      <img 
                        src={similarProduct.thumbnail_url || similarProduct.image} 
                        alt={similarProduct.title} 
                        className="max-h-full object-contain" 
                      />
                    ) : (
                      <Package className="w-12 h-12 text-gray-300" />
                    )}
                  </div>
                  <div className="p-5">
                    <h4 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">{similarProduct.title}</h4>
                    {similarProduct.description && (
                      <div className="mb-3 max-h-12 overflow-y-auto">
                        <div 
                          className="text-xs text-gray-500 prose prose-xs max-w-none line-clamp-2" 
                          dangerouslySetInnerHTML={{ __html: similarProduct.description }} 
                        />
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      {isSimilarFreeForMember ? (
                        <>
                          <span className="text-gray-400 font-bold text-lg line-through">
                            £{parseFloat(similarProduct.price).toFixed(2)}
                          </span>
                          <span className="font-bold" style={{ color: GREEN }}>FREE</span>
                        </>
                      ) : (
                        <span className="font-bold text-xl" style={{ color: GREEN }}>
                          £{similarUserPrice.toFixed(2)}
                        </span>
                      )}
                    </div>
                    
                    {/* Member hint for non-members */}
                    {!isMember && similarProduct.is_free_for_members && (
                      <p className="text-xs mt-2" style={{ color: GREEN }}>
                        <Users className="w-3 h-3 inline mr-1" />
                        Free for members
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;